/**
 * useObjectsGraph - Hook to transform Cypher query results into ReactFlow nodes and edges.
 *
 * Parses the AGG response format and extracts objects and relationships,
 * creating graph nodes with smart link URLs and edges for relationships.
 */

import { useMemo } from 'react';

import type { Node, Edge } from '@xyflow/react';
import { useAtomValue } from 'jotai';

import { getSmartLinkUrl, getDisplayName } from '../../../labs/home/utils/smartLinks';

import { ExpandedGroupsAtom, ViewportSizeAtom } from './atoms/visualizerAtoms';
import type { ObjectEdgeData } from './nodes/ObjectEdge';
import type { ObjectNodeData } from './nodes/ObjectNode';
import { RELATIONSHIP_COLORS } from './services/cypherQueryService';
import { AtiNodeMetadataMapAtom } from './services/twgSchemaService';
import { resolveNodeMetadataFromAri, type AtiNodeMetadata } from './utils/ariUtils';

type ColumnEntry = { key: string; value: any };
type EdgeNode = { __typename?: string; columns: ColumnEntry[] };
type ResultEdge = { node: EdgeNode };

export type AGGResponse = {
	data?: {
		graphStore?: {
			cypherQueryV2?: {
				edges: ResultEdge[];
			};
		};
	};
	errors?: Array<{ message: string }>;
};

// Layout constants — use assumed largest node size (maxWidth from ObjectNode styles)
const ASSUMED_NODE_WIDTH = 280;
const ASSUMED_NODE_HEIGHT = 70;
const NODE_HORIZONTAL_GAP = 40;
const NODE_VERTICAL_GAP = 30;
const NODES_PER_ROW = 4;
const COLLAPSED_MAX_NODES = 4;
const GROUP_PADDING_TOP = 40; // space for the group label
const GROUP_PADDING_HORIZONTAL = 20;
const GROUP_PADDING_BOTTOM = 20;
const GROUP_GAP = 50; // gap between groups in masonry layout
const HEADER_HEIGHT = 70;
void HEADER_HEIGHT; // space for the Results header
const HEADER_MARGIN_BOTTOM = 30;
void HEADER_MARGIN_BOTTOM; // gap between header and first group
const MASONRY_COLUMN_GAP = 50; // horizontal gap between masonry columns

// Legacy constants (kept for backward compatibility with createObjectNode index param)
const HORIZONTAL_SPACING = 300;
const VERTICAL_SPACING = 120;

/**
 * Checks if a value looks like a graph node.
 *
 * Accepts entities that have either:
 * - A typename (`__typename` / `typename`) AND an identifier (`id` / `ari` / `nodeId`)
 * - Just an ARI identifier (starting with `ari:`) — the typename will be
 *   resolved from the ATI embedded in the ARI at node-creation time.
 */
const isGraphNode = (value: any): boolean => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return false;
	}
	const hasId = value.id || value.ari || value.nodeId;
	if (!hasId) {
		return false;
	}
	// Accept if it has a typename, OR if the id is an ARI (typename can be resolved later)
	if (value.__typename || value.typename) {
		return true;
	}
	const id = value.id || value.ari || value.nodeId;
	return typeof id === 'string' && id.startsWith('ari:');
};

/**
 * Checks if a value looks like a relationship
 */
const isRelationship = (value: any): boolean => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) {
		return false;
	}
	return (
		value.__typename &&
		(value.sourceNode || value.source || value.from) &&
		(value.targetNode || value.target || value.to)
	);
};

/**
 * Extracts node ID from various formats
 */
const extractNodeId = (value: any): string => {
	if (typeof value === 'string') {
		return value;
	}
	return value?.id || value?.ari || value?.nodeId || String(value);
};

/**
 * Strips the `TypeName_` prefix from a property key if it matches the entity's __typename.
 * e.g. "ConfluencePage_title" → "title" when typename is "ConfluencePage".
 * Keys that don't have the prefix are returned as-is.
 */
const stripTypenamePrefix = (propKey: string, typename: string): string => {
	if (typename && propKey.startsWith(`${typename}_`)) {
		return propKey.substring(typename.length + 1);
	}
	return propKey;
};

/**
 * Normalizes an entity object by stripping typename prefixes from all property keys.
 * This produces a clean object where e.g. `ConfluencePage_title` becomes `title`,
 * enabling downstream functions (getSmartLinkUrl, getDisplayName) and the
 * metadata panel to work with clean labels.
 */
const normalizeEntityProperties = (obj: any, typename: string): Record<string, any> => {
	const normalized: Record<string, any> = {};
	for (const [rawKey, value] of Object.entries(obj)) {
		if (rawKey.startsWith('__') || rawKey === 'id' || rawKey === 'ari') {
			continue;
		}
		const cleanKey = stripTypenamePrefix(rawKey, typename);
		normalized[cleanKey] = value;
	}
	return normalized;
};

/**
 * Creates a ReactFlow node from a graph object.
 *
 * The node's typename and display name are resolved from the ARI as the
 * primary source of truth via the ATI node metadata map. The `__typename`
 * from the GraphQL response is only used as a fallback when ARI-based
 * resolution is not possible (e.g. non-ARI identifiers).
 */
const createObjectNode = (
	obj: any,
	key: string,
	index: number,
	existingNodeIds: Set<string>,
	atiNodeMap: Record<string, AtiNodeMetadata>,
): Node<ObjectNodeData> | null => {
	const id = extractNodeId(obj);

	// Skip if we already have this node or no valid ID
	if (!id || existingNodeIds.has(id)) {
		return null;
	}
	existingNodeIds.add(id);

	let typename = '';
	let typenameDisplay = '';

	// Primary: resolve from ARI via ATI metadata
	if (typeof id === 'string' && id.startsWith('ari:')) {
		const metadata = resolveNodeMetadataFromAri(id, atiNodeMap);
		if (metadata) {
			typename = metadata.typename;
			typenameDisplay = metadata.displayName;
		}
	}

	// Fallback: use GraphQL __typename if ARI resolution failed
	if (!typename) {
		typename = obj.__typename || obj.typename || 'Unknown';
		typenameDisplay = typename;
	}

	// Use the GraphQL __typename for property normalisation — the response
	// keys are prefixed with the hydration type (e.g. "AtlassianAccountUser_name"),
	// not the TWG node name ("IdentityUser"). Fall back to the ATI-resolved
	// typename for entities that lack __typename entirely.
	const normalisationTypename = obj.__typename || obj.typename || typename;

	// Normalize properties: strip TypeName_ prefix from keys
	// so "ConfluencePage_title" becomes "title" everywhere
	const normalized = normalizeEntityProperties(obj, normalisationTypename);

	// Build a clean entity for URL and display name extraction
	const cleanEntity = { ...normalized, __typename: normalisationTypename, id };
	const url = getSmartLinkUrl(cleanEntity) ?? null;
	const displayName = getDisplayName(key, cleanEntity) ?? null;

	// Extract properties (excluding internal fields and nested graph nodes)
	const properties: Record<string, any> = {};
	for (const [propKey, propValue] of Object.entries(normalized)) {
		// Skip nested objects that are graph nodes themselves
		if (isGraphNode(propValue)) {
			continue;
		}
		properties[propKey] = propValue;
	}

	// Calculate position in a grid layout
	const row = Math.floor(index / NODES_PER_ROW);
	const col = index % NODES_PER_ROW;

	return {
		id,
		type: 'objectNode',
		position: {
			x: col * HORIZONTAL_SPACING + 50,
			y: row * VERTICAL_SPACING + 50,
		},
		data: {
			id,
			typename,
			typenameDisplay,
			displayName,
			url,
			properties,
		},
	};
};

/**
 * Creates a ReactFlow edge from a relationship
 */
const createObjectEdge = (
	rel: any,
	index: number,
	existingEdgeIds: Set<string>,
): Edge<ObjectEdgeData> | null => {
	const sourceNode = rel.sourceNode || rel.source || rel.from;
	const targetNode = rel.targetNode || rel.target || rel.to;

	if (!sourceNode || !targetNode) {
		return null;
	}

	const sourceId = extractNodeId(sourceNode);
	const targetId = extractNodeId(targetNode);
	const edgeId = `${sourceId}-${targetId}-${index}`;

	if (existingEdgeIds.has(edgeId)) {
		return null;
	}
	existingEdgeIds.add(edgeId);

	const relationshipType = rel.__typename || rel.type || rel.relationshipType || 'RELATES_TO';

	// Extract relationship properties
	const properties: Record<string, any> = {};
	for (const [key, value] of Object.entries(rel)) {
		if (
			key.startsWith('__') ||
			key === 'sourceNode' ||
			key === 'source' ||
			key === 'from' ||
			key === 'targetNode' ||
			key === 'target' ||
			key === 'to' ||
			key === 'type' ||
			key === 'relationshipType'
		) {
			continue;
		}
		if (!isGraphNode(value)) {
			properties[key] = value;
		}
	}

	return {
		id: edgeId,
		type: 'objectEdge',
		source: sourceId,
		target: targetId,
		markerEnd: 'arrowclosed',
		data: {
			relationshipType,
			properties: Object.keys(properties).length > 0 ? properties : undefined,
		},
	};
};

/**
 * Extracts entities from a column value, handling the GraphQL union types
 */
const extractEntitiesFromValue = (value: any): any[] => {
	if (!value || typeof value !== 'object') {
		return [];
	}

	const entities: any[] = [];

	// Handle GraphStoreCypherQueryV2NodeList - has nodes array
	if (value.nodes && Array.isArray(value.nodes)) {
		for (const nodeItem of value.nodes) {
			// Each node has { id, data } where data contains the actual entity
			if (nodeItem.data && isGraphNode(nodeItem.data)) {
				entities.push(nodeItem.data);
			} else if (isGraphNode(nodeItem)) {
				entities.push(nodeItem);
			}
		}
		return entities;
	}

	// Handle GraphStoreCypherQueryV2AriNode - has id and data
	// The entity data may not have a top-level `id` field — the id is on
	// the parent wrapper. Merge them so downstream code can identify the node.
	if (value.data && value.id) {
		const entityData = value.data;
		if (isGraphNode(entityData)) {
			entities.push(entityData);
		} else if (entityData && typeof entityData === 'object') {
			// The data may lack both id and __typename (e.g. when GraphQL
			// hydration fails). Merge the wrapper id so the entity can be
			// identified — the typename will be resolved from the ARI later.
			entities.push({ ...entityData, id: value.id });
		}
		return entities;
	}

	// Handle scalar types (str_val, int_val, timestamp)
	if (value.str_val !== undefined || value.int_val !== undefined || value.timestamp !== undefined) {
		// These are scalar values, not graph nodes - skip them
		return [];
	}

	// Handle direct graph nodes
	if (isGraphNode(value)) {
		entities.push(value);
		return entities;
	}

	// Handle arrays
	if (Array.isArray(value)) {
		for (const item of value) {
			entities.push(...extractEntitiesFromValue(item));
		}
		return entities;
	}

	return entities;
};

function computeGroupWidth(visibleCount: number, nodesPerRow: number): number {
	const nodesInRow = Math.min(nodesPerRow, visibleCount);
	const contentWidth =
		nodesInRow * ASSUMED_NODE_WIDTH + Math.max(0, nodesInRow - 1) * NODE_HORIZONTAL_GAP;
	return contentWidth + GROUP_PADDING_HORIZONTAL * 2;
}

function computeGroupHeight(visibleCount: number, nodesPerRow: number): number {
	if (visibleCount === 0) {
		return GROUP_PADDING_TOP + GROUP_PADDING_BOTTOM;
	}
	const rows = Math.ceil(visibleCount / nodesPerRow);
	const contentHeight = rows * ASSUMED_NODE_HEIGHT + Math.max(0, rows - 1) * NODE_VERTICAL_GAP;
	return GROUP_PADDING_TOP + contentHeight + GROUP_PADDING_BOTTOM;
}

export type GroupDescriptor = {
	typename: string;
	allMembers: Node<ObjectNodeData>[];
	visibleMembers: Node<ObjectNodeData>[];
	totalCount: number;
	visibleCount: number;
	isExpanded: boolean;
	hasMore: boolean;
	width: number;
	height: number;
	color: string;
	nodesPerRow: number;
};

/**
 * Builds group descriptors from the raw groups map with a given nodesPerRow.
 * Preserves the original Map insertion order (stable across expand/collapse).
 */
function buildDescriptors(
	groupsMap: Map<string, Node<ObjectNodeData>[]>,
	expandedGroups: Set<string>,
	nodesPerRow: number,
	colors: readonly string[],
): GroupDescriptor[] {
	const descriptors: GroupDescriptor[] = [];
	let colorIndex = 0;

	// Dynamic collapsed limit: fill 2 rows of the chosen column width so
	// collapsed groups don't have awkward gaps (e.g. a 3-column layout
	// showing only 4 nodes in a 3+1 arrangement). Use at least the
	// original COLLAPSED_MAX_NODES to avoid showing fewer than before.
	const collapsedMax = Math.max(COLLAPSED_MAX_NODES, nodesPerRow * 2);

	for (const [typename, allMembers] of groupsMap) {
		const isExpanded = expandedGroups.has(typename);
		const hasMore = allMembers.length > collapsedMax;
		const visibleMembers = !isExpanded && hasMore ? allMembers.slice(0, collapsedMax) : allMembers;

		const color = colors[colorIndex % colors.length];
		colorIndex++;

		descriptors.push({
			typename,
			allMembers,
			visibleMembers,
			totalCount: allMembers.length,
			visibleCount: visibleMembers.length,
			isExpanded,
			hasMore,
			width: computeGroupWidth(visibleMembers.length, nodesPerRow),
			height: computeGroupHeight(visibleMembers.length, nodesPerRow),
			color,
			nodesPerRow,
		});
	}

	return descriptors;
}

type MasonryResult = {
	positions: Map<string, { x: number; y: number }>;
	totalWidth: number;
	totalHeight: number;
};

/**
 * Masonry placement with a given number of columns. Places groups in the
 * shortest column, preserving the input order so groups don't jump around
 * when one is expanded.
 */
function masonryPlace(
	descriptors: GroupDescriptor[],
	startY: number,
	numColumns: number,
): MasonryResult {
	if (descriptors.length === 0) {
		return { positions: new Map(), totalWidth: 0, totalHeight: startY };
	}

	const columnWidth = Math.max(...descriptors.map((d) => d.width));
	const columnHeights = new Array(numColumns).fill(startY);
	const positions = new Map<string, { x: number; y: number }>();

	for (const desc of descriptors) {
		let bestCol = 0;
		for (let c = 1; c < numColumns; c++) {
			if (columnHeights[c] < columnHeights[bestCol]) {
				bestCol = c;
			}
		}

		const x = bestCol * (columnWidth + MASONRY_COLUMN_GAP);
		const y = columnHeights[bestCol];
		positions.set(desc.typename, { x, y });

		columnHeights[bestCol] = y + desc.height + GROUP_GAP;
	}

	const totalWidth = numColumns * columnWidth + Math.max(0, numColumns - 1) * MASONRY_COLUMN_GAP;
	const totalHeight = Math.max(...columnHeights);

	return { positions, totalWidth, totalHeight };
}

/**
 * Tries multiple (nodesPerRow × numColumns) combinations and returns the one
 * that best fills the viewport at zoom ≤ 1.
 *
 * Optimisation strategy:
 *   1. Among layouts that fit at zoom ≥ 1 (content ≤ viewport), pick the one
 *      whose zoom is closest to 1 — i.e. the layout that fills the most
 *      viewport space without needing to zoom out.
 *   2. If NO layout fits at zoom ≥ 1 (all content is larger than the
 *      viewport), fall back to the layout with the highest zoom so the user
 *      has to zoom out the least.
 *
 * The candidate nodesPerRow range is derived dynamically from the largest
 * visible group size so the algorithm can explore wider, single-row-ish
 * layouts that better utilise panoramic viewports.
 */
function findBestLayout(
	groupsMap: Map<string, Node<ObjectNodeData>[]>,
	expandedGroups: Set<string>,
	startY: number,
	viewportWidth: number,
	viewportHeight: number,
): {
	descriptors: GroupDescriptor[];
	positions: Map<string, { x: number; y: number }>;
	nodesPerRow: number;
} {
	const groupCount = groupsMap.size;
	const maxColumns = Math.min(groupCount, 3);

	// Determine the largest visible member count across all groups so we
	// can build a candidate range that includes wider layouts.
	// Note: the actual collapsed limit is dynamic (nodesPerRow * 2), so for
	// candidate-range purposes we use the raw member count — the
	// buildDescriptors call inside the loop will apply the correct
	// per-candidate collapsed limit.
	let maxVisibleCount = 0;
	for (const [, members] of groupsMap) {
		maxVisibleCount = Math.max(maxVisibleCount, members.length);
	}

	// Candidates from 2 up to the largest visible count (capped at a
	// reasonable maximum to avoid degenerate single-row layouts).
	const maxCandidate = Math.min(Math.max(maxVisibleCount, 4), 12);
	const candidateNodesPerRow: number[] = [];
	for (let n = 2; n <= maxCandidate; n++) {
		candidateNodesPerRow.push(n);
	}

	// Track the best "fits" layout (zoom ≥ 1, closest to 1) and the best
	// "overflows" layout (zoom < 1, highest zoom) separately.
	let bestFitsResult: {
		descriptors: GroupDescriptor[];
		positions: Map<string, { x: number; y: number }>;
		nodesPerRow: number;
		zoom: number;
	} | null = null;

	let bestOverflowResult: {
		descriptors: GroupDescriptor[];
		positions: Map<string, { x: number; y: number }>;
		nodesPerRow: number;
		zoom: number;
	} | null = null;

	for (const nodesPerRow of candidateNodesPerRow) {
		const descs = buildDescriptors(groupsMap, expandedGroups, nodesPerRow, RELATIONSHIP_COLORS);

		for (let numColumns = 1; numColumns <= maxColumns; numColumns++) {
			const { positions, totalWidth, totalHeight } = masonryPlace(descs, startY, numColumns);
			const zoom = Math.min(viewportWidth / totalWidth, viewportHeight / totalHeight);

			if (zoom >= 1) {
				// Content fits — prefer the layout closest to zoom=1 (most fill)
				if (!bestFitsResult || zoom < bestFitsResult.zoom) {
					bestFitsResult = { descriptors: descs, positions, nodesPerRow, zoom };
				}
			} else {
				// Content overflows — prefer highest zoom (least zoom-out)
				if (!bestOverflowResult || zoom > bestOverflowResult.zoom) {
					bestOverflowResult = { descriptors: descs, positions, nodesPerRow, zoom };
				}
			}
		}
	}

	const best = bestFitsResult ?? bestOverflowResult!;
	return {
		descriptors: best.descriptors,
		positions: best.positions,
		nodesPerRow: best.nodesPerRow,
	};
}

/**
 * Computes grouped layout positions for nodes using an adaptive masonry algorithm.
 * Tries multiple (nodesPerRow, numColumns) combinations and picks the one that
 * maximises zoom for the given viewport. Group order is preserved across
 * expand/collapse so groups don't jump around.
 */
function computeGroupedLayout(
	objectNodes: Node<ObjectNodeData>[],
	expandedGroups: Set<string>,
	viewportWidth: number,
	viewportHeight: number,
): {
	positionedNodes: Node<ObjectNodeData>[];
} {
	if (objectNodes.length === 0) {
		return { positionedNodes: [] };
	}

	const groupsMap = new Map<string, Node<ObjectNodeData>[]>();
	for (const node of objectNodes) {
		const typename = node.data.typename || 'Unknown';
		let group = groupsMap.get(typename);
		if (!group) {
			group = [];
			groupsMap.set(typename, group);
		}
		group.push(node);
	}

	// No header — nodes start at y=0
	const startY = 0;
	const { descriptors, positions, nodesPerRow } = findBestLayout(
		groupsMap,
		expandedGroups,
		startY,
		viewportWidth,
		viewportHeight,
	);

	const positionedNodes: Node<ObjectNodeData>[] = [];

	for (const desc of descriptors) {
		const pos = positions.get(desc.typename)!;

		for (let i = 0; i < desc.visibleMembers.length; i++) {
			const row = Math.floor(i / nodesPerRow);
			const col = i % nodesPerRow;

			positionedNodes.push({
				...desc.visibleMembers[i],
				position: {
					x: pos.x + GROUP_PADDING_HORIZONTAL + col * (ASSUMED_NODE_WIDTH + NODE_HORIZONTAL_GAP),
					y: pos.y + GROUP_PADDING_TOP + row * (ASSUMED_NODE_HEIGHT + NODE_VERTICAL_GAP),
				},
			});
		}
	}

	return { positionedNodes };
}

/**
 * Main hook to transform query results into graph data.
 *
 * Returns object nodes grouped by typename with group rectangle nodes,
 * a results header annotation node, and edges for relationships.
 *
 * Internally reads ExpandedGroupsAtom, ViewportSizeAtom, and
 * AtiNodeMetadataMapAtom so the caller doesn't need to prop-drill them.
 * Computation is split into two phases:
 *   1. Parsing (depends only on queryResult + typenameDisplayMap)
 *   2. Layout  (depends on parsed nodes + expandedGroups + viewport)
 */
export const useObjectsGraph = (
	queryResult: AGGResponse | undefined | null,
): {
	nodes: Node<ObjectNodeData>[];
	edges: Edge<ObjectEdgeData>[];
} => {
	const expandedGroups = useAtomValue(ExpandedGroupsAtom);
	const viewportSize = useAtomValue(ViewportSizeAtom);
	const atiNodeMap = useAtomValue(AtiNodeMetadataMapAtom);

	// Phase 1: Parse the AGG response into raw nodes + edges.
	// Only recomputes when queryResult or atiNodeMap changes.
	const parsed = useMemo(() => {
		if (!queryResult?.data?.graphStore?.cypherQueryV2?.edges) {
			return { rawNodes: [] as Node<ObjectNodeData>[], edges: [] as Edge<ObjectEdgeData>[] };
		}

		const resultEdges = queryResult.data.graphStore.cypherQueryV2.edges;
		const rawNodes: Node<ObjectNodeData>[] = [];
		const edges: Edge<ObjectEdgeData>[] = [];
		const existingNodeIds = new Set<string>();
		const existingEdgeIds = new Set<string>();
		let nodeIndex = 0;
		let edgeIndex = 0;

		for (const resultEdge of resultEdges) {
			const columns = resultEdge.node?.columns || [];

			for (const column of columns) {
				const { key, value } = column;

				if (!value) {
					continue;
				}

				const entities = extractEntitiesFromValue(value);

				for (const entity of entities) {
					if (isRelationship(entity)) {
						const edge = createObjectEdge(entity, edgeIndex++, existingEdgeIds);
						if (edge) {
							edges.push(edge);
						}
						const sourceNode = entity.sourceNode || entity.source || entity.from;
						const targetNode = entity.targetNode || entity.target || entity.to;
						if (isGraphNode(sourceNode)) {
							const node = createObjectNode(
								sourceNode,
								key,
								nodeIndex,
								existingNodeIds,
								atiNodeMap,
							);
							if (node) {
								rawNodes.push(node);
								nodeIndex++;
							}
						}
						if (isGraphNode(targetNode)) {
							const node = createObjectNode(
								targetNode,
								key,
								nodeIndex,
								existingNodeIds,
								atiNodeMap,
							);
							if (node) {
								rawNodes.push(node);
								nodeIndex++;
							}
						}
					} else if (isGraphNode(entity)) {
						const node = createObjectNode(entity, key, nodeIndex, existingNodeIds, atiNodeMap);
						if (node) {
							rawNodes.push(node);
							nodeIndex++;
						}
					}
				}
			}
		}

		return { rawNodes, edges };
	}, [queryResult, atiNodeMap]);

	// Phase 2: Layout — run the masonry algorithm on the parsed nodes.
	// Recomputes when expandedGroups or viewport size changes without
	// re-parsing the AGG response.
	return useMemo(() => {
		if (parsed.rawNodes.length === 0 && parsed.edges.length === 0) {
			return { nodes: [], edges: [] };
		}

		const { positionedNodes } = computeGroupedLayout(
			parsed.rawNodes,
			expandedGroups,
			viewportSize.width,
			viewportSize.height,
		);

		return { nodes: positionedNodes as Node<any>[], edges: parsed.edges };
	}, [parsed, expandedGroups, viewportSize]);
};
